import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../common/apiClient';

interface CoffeeOrderStatus {
  hasOrder: boolean;
  orderPreview: string | null;
  deliveryDate: string | null;
  isLoading: boolean;
  error: string | null;
}

// Custom event name for coffee order updates
const COFFEE_ORDER_UPDATE_EVENT = 'coffeeOrderUpdate';

// Helper to dispatch coffee order update event
export const triggerCoffeeOrderUpdate = () => {
  window.dispatchEvent(new CustomEvent(COFFEE_ORDER_UPDATE_EVENT));
};

export const useCoffeeOrderStatus = (userEmail: string | undefined): CoffeeOrderStatus => {
  const [status, setStatus] = useState<CoffeeOrderStatus>({
    hasOrder: false,
    orderPreview: null,
    deliveryDate: null,
    isLoading: true,
    error: null,
  });

  const fetchOrderStatus = useCallback(async () => {
    if (!userEmail) {
      setStatus({ hasOrder: false, orderPreview: null, deliveryDate: null, isLoading: false, error: null });
      return;
    }

    try {
      const response = await apiClient.get<{ hasOrder: boolean; orderPreview: string | null; deliveryDate: string | null; error?: boolean }>(
        `/api/cafe/orders/next-day-check?email=${encodeURIComponent(userEmail)}`
      );

      // This endpoint also reports upstream failures in a successful HTTP response.
      // An unavailable check must not be treated as a confirmed missing order.
      if (response.error || typeof response.hasOrder !== 'boolean') {
        throw new Error('Coffee order status unavailable');
      }

      setStatus({
        hasOrder: response.hasOrder,
        orderPreview: response.orderPreview,
        deliveryDate: response.deliveryDate,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching coffee order status:', error);
      setStatus({
        hasOrder: false,
        orderPreview: null,
        deliveryDate: null,
        isLoading: false,
        error: 'Failed to fetch order status',
      });
    }
  }, [userEmail]);

  // Fetch on mount
  useEffect(() => {
    fetchOrderStatus();
  }, [fetchOrderStatus]);

  // Listen for coffee order update events
  useEffect(() => {
    const handleUpdate = () => {
      fetchOrderStatus();
    };

    window.addEventListener(COFFEE_ORDER_UPDATE_EVENT, handleUpdate);
    return () => window.removeEventListener(COFFEE_ORDER_UPDATE_EVENT, handleUpdate);
  }, [fetchOrderStatus]);

  return status;
};
